import { Component, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ImageUploadComponent } from '@app/shared/image-upload/image-upload.component';
import { UserSearchFilter } from '@app/_models';
import { UserService, LocationService, DashboardService, AuthenticationService } from '@app/_services';
import { environment } from '@environments/environment';
import { first } from 'rxjs/operators';

@Component({ selector: 'edit-profile', templateUrl: 'edit-profile.component.html' })
export class EditProfileComponent {

    @ViewChild('imageUpload') imageUpload: ImageUploadComponent;
    user: any;
    profileForm: FormGroup;
    loading = false;
    submitted = false;
    error = '';

    countries = [];
    cities = [];
    states = [];
    sectors = [];

    equityIsDynamicUrl = environment.equityIsDynamicUrl;

    validationMessages = {
        'firstName': [
          { type: 'required', message: 'Please fill your first name' },
        ],
        'lastName': [
          { type: 'required', message: 'Please fill your last name' }
        ],
        'aboutYou': [
            { type: 'required', message: 'Please tell us about you' }
        ],
        'company': [
            { type: 'required', message: 'Please enter your company name' }
        ],
        'companyRol': [
            { type: 'required', message: 'Please tell us about your company role' }
        ],
        'companyWebsite': [
            { type: 'required', message: 'Please enter your company website' },
            { type: 'pattern', message: 'Please enter a valid URL' }
        ],
        'aboutYourCompany': [
            { type: 'required', message: 'Please tell us about your company' }
        ],
        'country': [
            { type: 'required', message: 'Please select your country' },
        ],
        'city': [
            { type: 'required', message: 'Please select your city' },
        ],
        'state': [
            { type: 'required', message: 'Please select your state' },
        ],
        'sectors': [
            { type: 'required', message: 'Please select one or more sectors' },
        ]
      };

    constructor(private router: Router, 
        private userService: UserService, 
        private authenticationService: AuthenticationService,
        private locationService: LocationService,
        private dashboardService: DashboardService,
        private formBuilder: FormBuilder) {
        this.profileForm = this.formBuilder.group({
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            emailAddress: ['', Validators.required],
            aboutYou: [''],
            company: ['', Validators.required],
            companyRol: [''],
            companyWebsite: ['', Validators.pattern('(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?')],
            aboutYourCompany: [''],
            country: [{}, Validators.required],
            state: [{}],
            city: [{}, Validators.required],
            sectors: [[], Validators.required],
        }, {
            validators: [this.stateConditionallyRequiredValidator]
        });
    }

    stateConditionallyRequiredValidator : ValidatorFn = (formGroup : FormGroup) => {
        return this.states?.length > 0 && !this.states.map(x => x.id).includes(formGroup.controls['state'].value)
                ? { stateConditionallyRequired: true }
                : null;
    }

    ngOnInit() {

        this.locationService.getAllCountries().pipe(first()).subscribe(data => {
            this.countries = data;
        });

        this.dashboardService.getSectors().pipe(first()).subscribe(data => {
            this.sectors = data;
          });

        this.userService.getBy(this.authenticationService.currentUserValue.id).pipe(first()).subscribe(data => {
            this.user = data;

            let countryId = this.user.location.countryId;

            this.profileForm.setValue({
                firstName: this.user.firstName,
                lastName: this.user.lastName,
                emailAddress: this.user.emailAddress,
                aboutYou: this.user.aboutYou,
                company: this.user.company,
                companyRol: this.user.companyRol,
                companyWebsite: this.user.companyWebsite,
                aboutYourCompany: this.user.aboutYourCompany,
                country: this.user.location.countryId,
                city: this.user.location.cityId,
                state: this.user.location.stateId,
                sectors: this.user.sectors.map(s => { return s.id; })
            });

            if (this.user.location.stateId != null) {
                this.locationService.getStates(countryId).pipe(first()).subscribe(data => {
                    this.states = data;
                    this.locationService.getCitiesBySelectedStateId(this.user.location.stateId).pipe(first()).subscribe(cityData => {
                        this.cities = cityData;
                    });
                });
            } else {
                this.locationService.getCitiesBySelectedCountryId(countryId).pipe(first()).subscribe(cityData => {
                    this.cities = cityData;
                });
            }
            
            this.imageUpload.imageSrc = this.user.userImageData;
        });
    }

    get f() { return this.profileForm.controls; }

    onSubmit() {
        this.submitted = true;

        if (this.profileForm.invalid) {
            return;
        }

        this.loading = true;

        let formObj = this.profileForm.getRawValue();
        delete formObj.state;
        formObj.cityId = formObj.city;
        formObj.userImageData = this.imageUpload.imageSrc;
        formObj.userId = this.authenticationService.currentUserValue.id;
        this.userService.update(formObj).pipe(first()).subscribe(() => {
            this.router.navigate(['/dashboard/profile']);
        });
    }

    hasError(validation: any, controlName: string) {
        return this.profileForm.get(controlName).hasError(validation.type);
                // && (this.profileForm.get(controlName).dirty
                // || this.profileForm.get(controlName).touched);
    }

    countryValueChanged(value: any) {
        this.cities = [];
        this.states = [];
        this.profileForm.controls['state'].setValue(null);
        this.profileForm.controls['city'].setValue(null);

        if (!value || !value.id) {
            return;
        }
        let countryId = value.id;

        this.locationService.getStates(countryId).pipe(first()).subscribe(data => {
            if (data && data.length > 0) {
                this.states = data;
            } else {
                this.locationService.getCitiesBySelectedCountryId(countryId).pipe(first()).subscribe(data => {
                    this.cities = data;
                }); 
            }
        });
    }

    stateValueChanged(value: any) {
        this.cities = [];
        this.profileForm.controls['city'].setValue(null);

        if (!value || !value.id) {
            return;
        }

        let stateId = value.id;
        
        this.locationService.getCitiesBySelectedStateId(stateId).pipe(first()).subscribe(data => {
            this.cities = data;
        }); 
    }

    onConnectToSlack() {
        this.userService.markConnectToSlackStepAsComplete(this.user.userId).pipe(first()).subscribe(data => {
            console.log("markConnectToSlackStepAsComplete");
        });
    }
}